import { virtualRoomTileService, TileSyncResult, TileWrite } from '../../../../../../services/back4app/VirtualRoomTileService'
import { requireCurrentMapId } from '../../../../../../utils/currentMapId'

export interface TileChange {
    roomId: string
    tileX: number
    tileY: number
    tileType: 'room' | 'outline'
    operation: 'create' | 'update' | 'delete'
    timestamp: number
    retryCount: number
}

export interface TileSyncStatus {
    isOnline: boolean
    pendingChanges: number
    lastSyncTime: number | null
    syncErrors: string[]
    isSyncing: boolean
    batchSize: number
    syncInterval: number
    /** Tiles reached Cloud Code in this session, so the restart notice applies (SC-422). */
    savedThisSession: boolean
}

export interface TileSyncOptions {
    forceSync?: boolean
    batchSize?: number
    retryAttempts?: number
    retryDelay?: number
}

/**
 * Painting saves at most once this often (SC-477). A drag used to start a save per tile, and every
 * save reads all of the VR's stored tiles in Cloud Code. Changes pile up per VR and go out together.
 */
export const TILE_AUTO_SAVE_INTERVAL_MS = 30_000

/**
 * Longest the auto-save waits after saves that keep failing. Failed tiles stay queued, so without
 * a back-off a Cloud Code error that never clears (141 covers any plain Error) would cost a save
 * and its three retries every 30 s for as long as the editor is open.
 */
export const TILE_AUTO_SAVE_MAX_DELAY_MS = 10 * 60_000

/**
 * Most tiles one `saveVirtualRoomTiles` call carries (Cloud Code accepts 5,000). Every call reads
 * all of the VR's stored tiles, so a save sends each VR's changes in as few calls as it can; the
 * old 50 per call made a 200-tile drag four calls even after batching.
 */
const TILES_PER_SAVE_CALL = 2000

/**
 * Longest the editor waits for its last tiles to save when it closes or the admin leaves the room.
 * A Back4App that isn't answering must not trap the admin. Used by the painter's dispose and the
 * game menu's leave.
 */
export const MAP_EDITOR_FLUSH_TIMEOUT_MS = 5000

/**
 * Most `saveVirtualRoomTiles` calls one VR's changes may use in one save while looking for the
 * tiles Cloud Code refuses (see `saveRoomChanges`). Isolating one bad tile in 2,000 takes about
 * 2*log2(2000) = 22 calls, so this fits several bad tiles but never hammers Back4App for a batch
 * that is wrong all the way through.
 */
const MAX_SAVE_CALLS_PER_ROOM = 200

/**
 * Parse codes `saveVirtualRoomTiles` answers with when it refuses the change itself: 142 (a rule
 * such as "Every tile must be inside the map"), 119 (not a platform admin), 101 (the map isn't
 * registered). Sending the same change again gets the same answer, so these are dropped and shown.
 * Everything else stays queued: no connection, timeout, 5xx, an expired session, and 141, which
 * Parse returns for any plain Error in Cloud Code, including a transient DB or Back4App failure.
 */
const PERMANENT_SAVE_ERROR_CODES = new Set([101, 119, 142])

export function isPermanentSaveError(error: unknown): boolean {
    const code = (error as { code?: unknown } | null | undefined)?.code
    return typeof code === 'number' && PERMANENT_SAVE_ERROR_CODES.has(code)
}

/**
 * Refusals that don't depend on which tiles are in the call, so no half of it can pass: 119 and
 * 101 by code, and the three 142 rules about the call's ids (`cloud/virtualRoomManager.js`
 * `loadMap`, `assertVirtualRoomId`, `assertOnMap`). Cloud Code gives 142 no sub-code, so those
 * are told by their text. The text only saves calls: a 142 that doesn't match (including one whose
 * wording changed) is still split down to single tiles, which stays correct, just dearer.
 */
const WHOLE_CALL_RULE_MESSAGE = /mapId is required|virtualRoomId must be|already used on another map/

function isWholeCallRefusal(error: unknown): boolean {
    const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown }
    if (code === 119 || code === 101) return true
    return code === 142 && typeof message === 'string' && WHOLE_CALL_RULE_MESSAGE.test(message)
}

export class TileSyncManager {
    private pendingChanges: Map<string, TileChange> = new Map()
    // What the running save took off the queue. Until it ends those tiles are neither pending nor
    // stored, so readers of "what is painted" (getUnsavedChanges) need it too.
    private savingChanges: Map<string, TileChange> = new Map()
    private syncStatus: TileSyncStatus = {
        isOnline: navigator.onLine,
        pendingChanges: 0,
        lastSyncTime: null,
        syncErrors: [],
        isSyncing: false,
        batchSize: TILES_PER_SAVE_CALL,
        syncInterval: TILE_AUTO_SAVE_INTERVAL_MS,
        savedThisSession: false
    }
    // The one auto-save timer: set by the first pending change, never by later ones.
    private autoSaveTimer: ReturnType<typeof setTimeout> | null = null
    // Saves in a row whose tiles still failed after every retry; spaces out the auto-save.
    private failedSavesInRow = 0
    // Syncs run one after another, so a flush never overlaps an auto-save and an older change
    // can't land after a newer one for the same tile.
    private syncChain: Promise<unknown> = Promise.resolve()
    private activeSyncs = 0
    // Set when the page is leaving while a save runs: its retries then go out without the pause.
    private hurryRetries = false
    private wakeRetryWait: (() => void) | null = null
    // Tiles of a VR that was deleted, or of a cleared queue, must not come back when a save that
    // started before the clear fails and re-queues them.
    private discardCount = 0
    private clearedAllAt = 0
    private roomClearedAt: Map<string, number> = new Map()
    private lifecycleListenersInstalled = false
    private readonly MAX_RETRY_ATTEMPTS = 3
    private readonly RETRY_DELAY = 2000 // 2 seconds
    
    // Cache for loaded tiles to avoid duplicate queries
    private loadedTilesCache: Map<string, Array<{ roomId: string; tileX: number; tileY: number; tileType: 'room' | 'outline' }>> = new Map()
    private cacheExpiry: Map<string, number> = new Map()
    private readonly CACHE_DURATION = 2 * 60 * 1000 // 2 minutes cache for loaded tiles

    constructor() {
        this.setupOnlineOfflineHandling()
    }

    /**
     * Add a tile change to the sync queue
     */
    addTileChange(roomId: string, tileX: number, tileY: number, tileType: 'room' | 'outline', operation: 'create' | 'update' | 'delete'): void {
        const key = this.getTileKey(roomId, tileX, tileY)
        const change: TileChange = {
            roomId,
            tileX,
            tileY,
            tileType,
            operation,
            timestamp: Date.now(),
            retryCount: 0
        }

                    // If there's already a pending change for this tile, merge operations
            const existing = this.pendingChanges.get(key)
            if (existing) {
                // If we're deleting, that takes precedence
                if (operation === 'delete') {
                    change.operation = 'delete'
                } else if (existing.operation === 'delete' && (operation === 'create' || operation === 'update')) {
                    // If existing was delete but new is create/update, change to create
                    change.operation = 'create'
                }
                // Otherwise keep the existing operation
            }

        this.pendingChanges.set(key, change)
        this.syncStatus.pendingChanges = this.pendingChanges.size

        this.scheduleAutoSave()
        this.installLifecycleFlush()
    }

    /**
     * Add multiple tile changes at once (for bulk operations)
     */
    addTileChanges(changes: Array<{ roomId: string; tileX: number; tileY: number; tileType: 'room' | 'outline'; operation: 'create' | 'update' | 'delete' }>): void {
        changes.forEach(change => {
            this.addTileChange(change.roomId, change.tileX, change.tileY, change.tileType, change.operation)
        })
    }

    /**
     * Force sync all pending tile changes
     */
    async forceSync(options: TileSyncOptions = {}): Promise<TileSyncResult> {
        const { forceSync = true, batchSize = this.syncStatus.batchSize, retryAttempts = this.MAX_RETRY_ATTEMPTS } = options

        if (!this.syncStatus.isOnline) {
            return {
                success: false,
                syncedCount: 0,
                errorCount: this.pendingChanges.size,
                errors: ['Offline mode - cannot sync tiles to database']
            }
        }

        // An explicit save replaces the pending timer; whatever is painted after it starts a new one.
        this.stopAutoSave()
        try {
            return await this.syncPendingChanges(retryAttempts, batchSize)
        } finally {
            if (this.pendingChanges.size > 0) this.scheduleAutoSave()
        }
    }

    /**
     * Saves everything pending now, without waiting for the 30 s timer. For leaving the editor,
     * unmounting the painter, switching VRs and hiding or closing the tab.
     *
     * `hurry` is for a caller that is leaving and bounds the wait with `MAP_EDITOR_FLUSH_TIMEOUT_MS`:
     * neither the save in flight nor the one this starts then pauses between retries, because a 2 s
     * pause per retry runs past that bound and the painter's dispose would clear tiles a retry could
     * still have saved. Without it (a VR switch, the painter unmounting) nothing is waiting and the
     * pauses stay: three retries back to back would all fall inside one network blip and push the
     * auto-save back-off up for nothing.
     */
    flush({ hurry = false }: { hurry?: boolean } = {}): Promise<TileSyncResult> {
        if (this.pendingChanges.size === 0 && this.activeSyncs === 0) {
            return Promise.resolve({ success: true, syncedCount: 0, errorCount: 0, errors: [] })
        }
        // A save in flight has taken its changes off the queue, so this waits for it (the sync
        // chain), then saves whatever it put back. forceSync registers its save before its first
        // await, so the hurry covers both saves and lasts until the last of them ends.
        const save = this.forceSync()
        if (hurry && this.activeSyncs > 0) this.hurryActiveSave()
        return save
    }

    /**
     * Sync pending tile changes in batches
     */
    private syncPendingChanges(retryAttempts: number = this.MAX_RETRY_ATTEMPTS, batchSize: number = this.syncStatus.batchSize): Promise<TileSyncResult> {
        this.activeSyncs++
        this.syncStatus.isSyncing = true
        const run = this.syncChain.then(() => this.runSync(retryAttempts, batchSize))
        this.syncChain = run.catch(() => undefined)
        return run
            .then((result) => {
                // Every save path reports the same way, not just the explicit one.
                this.syncStatus.lastSyncTime = Date.now()
                this.syncStatus.pendingChanges = this.pendingChanges.size
                // A run that found nothing to save (it queued behind one that took the changes)
                // must not wipe the errors that run reported.
                if (result.syncedCount > 0 || result.errorCount > 0) this.syncStatus.syncErrors = result.errors
                // Clear the loaded tiles cache whenever something was stored, even if other tiles
                // failed: a partial save leaves the cache without the tiles that did save.
                if (result.syncedCount > 0) this.clearLoadedTilesCache()
                return result
            })
            .finally(() => {
                this.activeSyncs--
                if (this.activeSyncs === 0) {
                    this.syncStatus.isSyncing = false
                    this.hurryRetries = false
                }
            })
    }

    private async runSync(retryAttempts: number, batchSize: number): Promise<TileSyncResult> {
        const result: TileSyncResult = {
            success: true,
            syncedCount: 0,
            errorCount: 0,
            errors: []
        }

        if (this.pendingChanges.size === 0) {
            return result
        }

        const mapId = requireCurrentMapId()
        const startedAt = this.discardCount

        // Only what was pending when the save began is saved, and only its failures are retried.
        // A tile painted meanwhile stays queued for the next save: picking it up in a retry would
        // make a second save inside the 30 s window.
        // Grouped by VR so that, with `batchSize` large, each VR is one call.
        let changesToProcess = Array.from(this.pendingChanges.values())
            .sort((a, b) => (a.roomId < b.roomId ? -1 : a.roomId > b.roomId ? 1 : 0))
        this.pendingChanges.clear()
        this.savingChanges = new Map(
            changesToProcess.map((change) => [this.getTileKey(change.roomId, change.tileX, change.tileY), change] as const)
        )

        // Cloud Code's refusals are final: they are reported and dropped, not retried.
        let rejectedCount = 0
        const rejectedErrors: string[] = []
        // Only the last attempt's errors are reported: an earlier one that a retry fixed is not.
        let lastAttemptErrors: string[] = []

        for (let attempt = 0; attempt <= retryAttempts; attempt++) {
            const failed: TileChange[] = []
            const attemptErrors: string[] = []

            // Process changes in batches
            for (let i = 0; i < changesToProcess.length; i += batchSize) {
                const batch = changesToProcess.slice(i, i + batchSize)

                try {
                    const batchResult = await this.processBatch(batch, mapId)
                    result.syncedCount += batchResult.syncedCount
                    failed.push(...batchResult.failedChanges)
                    attemptErrors.push(...batchResult.errors)
                    rejectedCount += batchResult.rejectedChanges.length
                    rejectedErrors.push(...batchResult.rejectedErrors)
                } catch (error) {
                    console.error('[TileSyncManager] Batch processing error:', error)
                    attemptErrors.push(`Batch processing failed: ${error}`)
                    failed.push(...batch)
                }
            }

            failed.forEach(change => { change.retryCount++ })
            changesToProcess = failed
            lastAttemptErrors = attemptErrors
            if (changesToProcess.length === 0) {
                break
            }

            if (attempt < retryAttempts) {
                await this.waitToRetry()
            }
        }

        // Whatever still fails goes back on the queue for the next window (or the next flush), so
        // a dropped connection never loses painted tiles. Failing again pushes that window back.
        this.requeue(changesToProcess, startedAt)
        this.savingChanges = new Map()
        this.failedSavesInRow = changesToProcess.length > 0 ? this.failedSavesInRow + 1 : 0
        result.errorCount = changesToProcess.length + rejectedCount
        result.errors = [...rejectedErrors, ...lastAttemptErrors]
        result.success = result.errorCount === 0

        this.syncStatus.pendingChanges = this.pendingChanges.size
        return result
    }

    /**
     * Puts failed changes back without overwriting a change to the same tile made since the save
     * began: the newer one is what the admin sees. A failed delete or update may have left the
     * stored tile as it was, so a newer 'create' becomes an update (remove, then add) rather than
     * being skipped by Cloud Code as already stored.
     */
    private requeue(changes: TileChange[], startedAt: number): void {
        for (const change of changes) {
            if (this.clearedAllAt > startedAt || (this.roomClearedAt.get(change.roomId) ?? 0) > startedAt) continue
            const key = this.getTileKey(change.roomId, change.tileX, change.tileY)
            const newer = this.pendingChanges.get(key)
            if (!newer) {
                this.pendingChanges.set(key, change)
            } else if (newer.operation === 'create' && change.operation !== 'create') {
                newer.operation = 'update'
            }
        }
    }

    /** The pause before a retry. Cut short when the page is leaving. */
    private waitToRetry(): Promise<void> {
        if (this.hurryRetries) return Promise.resolve()
        return new Promise((resolve) => {
            let timer: ReturnType<typeof setTimeout> | undefined
            const done = () => {
                clearTimeout(timer)
                this.wakeRetryWait = null
                resolve()
            }
            this.wakeRetryWait = done
            timer = setTimeout(done, this.RETRY_DELAY)
        })
    }

    /** For a page that is leaving: the running save retries at once, as the page may not outlast a pause. */
    private hurryActiveSave(): void {
        this.hurryRetries = true
        this.wakeRetryWait?.()
    }

    /**
     * Saves a batch with one `saveVirtualRoomTiles` call per VR, all VRs at the same time: a page
     * that is closing may only get its first request out, so VRs must not wait for each other.
     * A failed call fails all of that VR's changes, which are retried. If Cloud Code refused them
     * (see `saveRoomChanges`) only the refused changes are rejected.
     */
    private async processBatch(changes: TileChange[], mapId: string): Promise<{
        syncedCount: number
        errors: string[]
        failedChanges: TileChange[]
        rejectedChanges: TileChange[]
        rejectedErrors: string[]
    }> {
        const byRoom = new Map<string, TileChange[]>()
        for (const change of changes) {
            const list = byRoom.get(change.roomId) || []
            list.push(change)
            byRoom.set(change.roomId, list)
        }

        const outcomes = await Promise.all(
            Array.from(byRoom, ([roomId, roomChanges]) => this.saveRoomChanges(roomId, roomChanges, mapId))
        )

        const result = {
            syncedCount: 0,
            errors: [] as string[],
            failedChanges: [] as TileChange[],
            rejectedChanges: [] as TileChange[],
            rejectedErrors: [] as string[]
        }
        for (const outcome of outcomes) {
            result.syncedCount += outcome.syncedCount
            result.errors.push(...outcome.errors)
            result.failedChanges.push(...outcome.failedChanges)
            result.rejectedChanges.push(...outcome.rejectedChanges)
            result.rejectedErrors.push(...outcome.rejectedErrors)
        }
        return result
    }

    /**
     * Saves one VR's changes. If Cloud Code refuses the call for a reason about its tiles (142),
     * the changes are split in half and each half is tried again, down to single changes, so
     * one bad tile does not take the other tiles of the batch (up to 2,000) with it. Only the
     * changes still refused alone are rejected. A refusal that isn't about the tiles
     * (`isWholeCallRefusal`) rejects the whole call at once. A failure that may pass (no connection, 5xx)
     * stops the search: the rest stays queued instead of making more calls into a dead connection.
     * `MAX_SAVE_CALLS_PER_ROOM` bounds the calls; what is left unchecked is rejected.
     */
    private async saveRoomChanges(roomId: string, roomChanges: TileChange[], mapId: string): Promise<{
        syncedCount: number
        errors: string[]
        failedChanges: TileChange[]
        rejectedChanges: TileChange[]
        rejectedErrors: string[]
    }> {
        const outcome = {
            syncedCount: 0,
            errors: [] as string[],
            failedChanges: [] as TileChange[],
            rejectedChanges: [] as TileChange[],
            rejectedErrors: [] as string[]
        }
        let callsLeft = MAX_SAVE_CALLS_PER_ROOM
        let transientError: unknown = null
        let firstRefusal = ''

        const save = async (chunk: TileChange[]): Promise<void> => {
            if (transientError) {
                outcome.failedChanges.push(...chunk)
                return
            }
            if (callsLeft <= 0) {
                outcome.rejectedChanges.push(...chunk)
                firstRefusal = firstRefusal || 'too many tiles were refused to check them one by one'
                return
            }
            callsLeft--
            try {
                await this.sendRoomChanges(roomId, chunk, mapId)
                outcome.syncedCount += chunk.length
                this.syncStatus.savedThisSession = true
            } catch (error) {
                if (!isPermanentSaveError(error)) {
                    transientError = error
                    outcome.errors.push(`Failed to save ${chunk.length} tiles for room ${roomId}: ${error}`)
                    outcome.failedChanges.push(...chunk)
                } else if (chunk.length === 1 || isWholeCallRefusal(error)) {
                    // Splitting a refusal that isn't about the tiles only repeats it: 5 VRs of
                    // 2,000 tiles refused with 119 cost 1,000 calls to reject what one call showed.
                    outcome.rejectedChanges.push(...chunk)
                    firstRefusal = firstRefusal || (error as Error).message || String(error)
                } else {
                    const middle = Math.ceil(chunk.length / 2)
                    await save(chunk.slice(0, middle))
                    await save(chunk.slice(middle))
                }
            }
        }

        await save(roomChanges)

        if (outcome.rejectedChanges.length > 0) {
            const others = outcome.syncedCount > 0 ? ` (${outcome.syncedCount} other tiles saved)` : ''
            outcome.rejectedErrors.push(
                `Rejected ${outcome.rejectedChanges.length} tiles for room ${roomId}: ${firstRefusal}${others}`
            )
        }
        return outcome
    }

    private async sendRoomChanges(roomId: string, roomChanges: TileChange[], mapId: string): Promise<void> {
        const add: TileWrite[] = []
        const remove: TileWrite[] = []
        for (const change of roomChanges) {
            const tile = { x: change.tileX, y: change.tileY }
            // Cloud Code keeps a tile it already has, so an update replaces the tile.
            if (change.operation !== 'create') remove.push(tile)
            if (change.operation !== 'delete') add.push({ ...tile, tileType: change.tileType })
        }
        if (roomChanges.some((c) => c.operation === 'update')) {
            await virtualRoomTileService.saveTiles(mapId, roomId, [], remove)
            await virtualRoomTileService.saveTiles(mapId, roomId, add, [])
        } else {
            await virtualRoomTileService.saveTiles(mapId, roomId, add, remove)
        }
    }

    /**
     * Load tiles from database for a specific room
     */
    async loadTilesForRoom(roomId: string): Promise<Array<{ tileX: number; tileY: number; tileType: 'room' | 'outline' }>> {
        try {
            const tiles = await virtualRoomTileService.getTilesByRoomId(roomId)
            return tiles.map(tile => ({
                tileX: tile.tileX,
                tileY: tile.tileY,
                tileType: tile.tileType
            }))
        } catch (error) {
            console.error('[TileSyncManager] Error loading tiles for room:', roomId, error)
            return []
        }
    }

    /**
     * All of a map's tiles, cached briefly so the editor and the game don't query twice.
     * A failed query returns [] unless `throwOnError` is set: the game must tell "no tiles" from
     * "not read" (a failure is never cached either way).
     */
    async loadTilesForMap(
        mapId: string,
        options?: { throwOnError?: boolean }
    ): Promise<Array<{ roomId: string; tileX: number; tileY: number; tileType: 'room' | 'outline' }>> {
        try {
            const cached = this.loadedTilesCache.get(mapId)
            const expiry = this.cacheExpiry.get(mapId)
            if (cached && expiry && expiry > Date.now()) {
                return cached
            }

            const tiles = await virtualRoomTileService.getTilesByMapId(mapId)
            const mappedTiles = tiles.map(tile => ({
                roomId: tile.roomId,
                tileX: tile.tileX,
                tileY: tile.tileY,
                tileType: tile.tileType
            }))
            this.loadedTilesCache.set(mapId, mappedTiles)
            this.cacheExpiry.set(mapId, Date.now() + this.CACHE_DURATION)
            return mappedTiles
        } catch (error) {
            console.error('[TileSyncManager] Error loading tiles for map:', mapId, error)
            if (options?.throwOnError) throw error
            return []
        }
    }

    /**
     * Clear all pending changes for a specific room
     */
    clearPendingChangesForRoom(roomId: string): void {
        const keysToDelete: string[] = []
        this.pendingChanges.forEach((change, key) => {
            if (change.roomId === roomId) {
                keysToDelete.push(key)
            }
        })

        keysToDelete.forEach(key => {
            this.pendingChanges.delete(key)
        })
        // A save in flight still sends them, but the VR is gone: nothing should draw them.
        this.savingChanges.forEach((change, key) => {
            if (change.roomId === roomId) this.savingChanges.delete(key)
        })
        this.roomClearedAt.set(roomId, ++this.discardCount)

        this.syncStatus.pendingChanges = this.pendingChanges.size
    }

    /**
     * Clear all pending changes
     */
    clearAllPendingChanges(): void {
        this.pendingChanges.clear()
        this.savingChanges = new Map()
        this.clearedAllAt = ++this.discardCount
        this.syncStatus.pendingChanges = 0
        this.syncStatus.syncErrors = []
    }

    clearLoadedTilesCache(mapId?: string): void {
        if (mapId) {
            this.loadedTilesCache.delete(mapId)
            this.cacheExpiry.delete(mapId)
        } else {
            this.loadedTilesCache.clear()
            this.cacheExpiry.clear()
        }
    }

    /**
     * Get cache statistics for loaded tiles
     */
    getLoadedTilesCacheStats(): { size: number; entries: string[]; totalTiles: number } {
        const entries = Array.from(this.loadedTilesCache.keys())
        const totalTiles = Array.from(this.loadedTilesCache.values()).reduce((sum, tiles) => sum + tiles.length, 0)
        return {
            size: this.loadedTilesCache.size,
            entries,
            totalTiles
        }
    }

    /**
     * Get sync status
     */
    getSyncStatus(): TileSyncStatus {
        return { ...this.syncStatus }
    }

    /**
     * Get pending changes count
     */
    getPendingChangesCount(): number {
        return this.pendingChanges.size
    }

    /**
     * Get detailed pending changes
     */
    getPendingChanges(): TileChange[] {
        return Array.from(this.pendingChanges.values())
    }

    /**
     * Changes not yet stored: those of the save in flight, then the pending ones (a newer change
     * to the same tile wins). Read-only copy, for drawing what the admin painted before it is saved.
     */
    getUnsavedChanges(): TileChange[] {
        const merged = new Map(this.savingChanges)
        this.pendingChanges.forEach((change, key) => merged.set(key, change))
        return Array.from(merged.values())
    }

    /**
     * Setup online/offline event handling
     */
    private setupOnlineOfflineHandling(): void {
        window.addEventListener('online', () => {
            this.syncStatus.isOnline = true
            this.syncInBackground('Sync on reconnect')
        })

        window.addEventListener('offline', () => {
            this.syncStatus.isOnline = false
        })
    }

    /**
     * Starts the one auto-save timer if it isn't running. A later change joins the batch the
     * timer will send; it never starts another timer (a drag used to start one per tile).
     */
    private scheduleAutoSave(): void {
        if (this.autoSaveTimer || !this.syncStatus.isOnline) return
        this.autoSaveTimer = setTimeout(() => {
            this.autoSaveTimer = null
            if (this.pendingChanges.size === 0 || !this.syncStatus.isOnline) return
            this.syncInBackground('Auto-save')
        }, this.autoSaveDelayMs())
    }

    /**
     * 30 s, and once saves fail twice in a row, twice as long for each further failure, up to
     * `TILE_AUTO_SAVE_MAX_DELAY_MS`. One failure keeps the 30 s: a brief outage costs no delay.
     * A save that goes through resets it; flush and forceSync never wait for it.
     */
    private autoSaveDelayMs(): number {
        const doublings = Math.max(0, this.failedSavesInRow - 1)
        return Math.min(this.syncStatus.syncInterval * 2 ** doublings, TILE_AUTO_SAVE_MAX_DELAY_MS)
    }

    private syncInBackground(label: string): void {
        this.syncPendingChanges()
            .catch((error) => console.error(`[TileSyncManager] ${label} failed:`, error))
            .finally(() => {
                // Changes made during the save, or ones that failed, wait for the next window.
                if (this.pendingChanges.size > 0) this.scheduleAutoSave()
            })
    }

    /** Stops the auto-save timer. Pending changes stay queued. */
    stopAutoSave(): void {
        if (this.autoSaveTimer) {
            clearTimeout(this.autoSaveTimer)
            this.autoSaveTimer = null
        }
    }

    /**
     * A closed or hidden tab can't wait for the timer. Best effort: the page may go before the
     * request does, and a hidden tab that comes back keeps painting with a fresh timer.
     */
    private flushNow(): void {
        this.stopAutoSave()
        if (!this.syncStatus.isOnline) return
        // A save in flight has already taken its changes off the queue: hurry its retries, and
        // queue a save behind it for the changes it puts back. A tab that was only hidden keeps
        // whatever still fails for the next window.
        if (this.activeSyncs > 0) this.hurryActiveSave()
        if (this.pendingChanges.size > 0 || this.activeSyncs > 0) this.syncInBackground('Flush on leave')
    }

    private readonly onPageHide = () => this.flushNow()

    private readonly onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') this.flushNow()
    }

    private installLifecycleFlush(): void {
        if (this.lifecycleListenersInstalled || typeof window === 'undefined') return
        this.lifecycleListenersInstalled = true
        window.addEventListener('pagehide', this.onPageHide)
        document.addEventListener('visibilitychange', this.onVisibilityChange)
    }

    private removeLifecycleFlush(): void {
        if (!this.lifecycleListenersInstalled) return
        this.lifecycleListenersInstalled = false
        window.removeEventListener('pagehide', this.onPageHide)
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }

    /**
     * Generate tile key for tracking
     */
    private getTileKey(roomId: string, tileX: number, tileY: number): string {
        return `${roomId}_${tileX}_${tileY}`
    }

    /**
     * Dispose of the manager
     */
    dispose(): void {
        this.stopAutoSave()
        this.removeLifecycleFlush()
        // The singleton outlives the editor; the next session starts at 30 s.
        this.failedSavesInRow = 0
        this.clearAllPendingChanges()
        this.clearLoadedTilesCache()
    }
}

// Create and export singleton instance
export const tileSyncManager = new TileSyncManager() 